import { observable, action, computed, autorun } from "mobx";
import { BaseStore, Delay } from "./BaseStore";
import { CustomerEndpoints } from "../endpoints/CustomerEndpoints";
import { Customer, Contact, CreditApplicationState } from "../model/Customer";
import { MultiPatchRequest, PatchRequest } from "../requests/PatchRequests";
import {
    AgentAuthorizationRequest,
    RevokeAgentAuthorizationRequest,
    CreditApplicationRequest,
    InsuranceRecordRequest
} from "src/requests/CustomerRequests";
import { SettlementRequest, InvoiceRequest, SettlementEditRequest } from "src/requests/FinancialRequests";

export class CustomerStore extends BaseStore {
    @observable private _customerId: number;
    @observable private _customer: Customer;

    constructor(private customerEndpoints: CustomerEndpoints, customer?: Customer) {
        super();
        // If we're provided with a customer, then set the store up
        if (customer) {
            this.manualRefresh(customer);
        }
    }

    @action async updateSubjectId(id: number) {
        this._customerId = id;
    }

    @action async manualRefresh(customer: Customer) {
        this._customerId = customer.id;
        this._customer = customer;
        this.setReady();
    }

    @action async refresh(): Promise<Customer> {
        this.setLoading();
        try {
            this._customer = await this.customerEndpoints.getCustomer(this._customerId);

            this.setReady();
            return this._customer;
        } catch (e) {
            this.setError(e ? e.message || e : "An unknown error occurred");
            throw e;
        }
    }

    @action updateFieldsByObject(fields: { [key: string]: string | number | null }): Promise<Customer> {
        const requests = Object.keys(fields).map(key => {
            return { field: key, value: `${fields[key]}` };
        });
        return this.updateFields(requests);
    }

    @action async updateFields(fields: Array<PatchRequest>): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editCustomer(this._customerId, { requests: fields });
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    // Add Contat to Business
    @action async addContactToBusiness(contactId: number, title: string): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.addContactToBusiness(this._customerId, { contactId, title });
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async removeContactFromBusiness(contactId: number): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.removeContactFromBusiness(this._customerId, contactId);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async createCreditApplication(request: CreditApplicationRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.createCreditApplication(this._customerId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editCreditApplication(applicationId: number, request: CreditApplicationRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editCreditApplication(applicationId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async createAgentAuthorization(request: AgentAuthorizationRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.createAgentAuthorization(request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editAgentAuthorization(agentAuthorizationId: number, request: AgentAuthorizationRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editAgentAuthorization(agentAuthorizationId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async revokeAgentAuthorization(agentAuthorizationId: number, request: RevokeAgentAuthorizationRequest): Promise<Customer> {
        this.setLoading();
        try {
            await this.customerEndpoints.revokeAgentAuthorization(agentAuthorizationId, request);
            return this.refresh();
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async creditApplicationStateChange(creditApplicationId: number, state: CreditApplicationState): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.creditApplicationStateChange(creditApplicationId, state);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async createInsuranceRecord(request: InsuranceRecordRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.createInsuranceRecord(this._customerId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editInsuranceRecord(insuranceId: number, request: InsuranceRecordRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editInsuranceRecord(insuranceId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async cancelInsuranceRecord(insuranceId: number): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.cancelInsuranceRecord(insuranceId);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @computed get customer(): Customer | undefined {
        return this._customer;
    }

    @action async createSettlement(request: SettlementRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.createSettlement(this._customerId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editSettlementDetails(settlememtId: number, request: SettlementEditRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editSettlementDetails(settlememtId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async deleteSettlement(settlememtId: number): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.deleteSettlement(settlememtId);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async createInvoice(request: InvoiceRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.createInvoice(this._customerId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editInvoice(invoiceId: number, request: InvoiceRequest): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.editInvoice(invoiceId, request);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async voidInvoice(invoiceId: number): Promise<Customer> {
        this.setLoading();
        try {
            const customer = await this.customerEndpoints.voidInvoice(invoiceId);
            this._customer = customer;
            return customer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async downloadInvoice(invoiceIds: Array<number>): Promise<Blob> {
        this.setLoading();
        try {
            const blob = await this.customerEndpoints.downloadInvoice(invoiceIds);
            return blob;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async printStatement(to: string, from: string): Promise<Blob> {
        this.setLoading();
        try {
            const blob = await this.customerEndpoints.printStatement(this._customer.id, to, from);
            return blob;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/CustomerStore.ts